import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as query from '../../../query'
import * as expressionNode from '../../node'
import type { FnHelper } from '../helper'
import { Key } from '../key'
import * as fnNode from '../node'

/**
 * Retrieves the value of a variable. Useful for creating resuable saved queries
 *
 * @param alias Alias of the desired variable
 * @example
 * // Query
 * {
 *   variables: {
 *     minVal: 100,
 *   }
 * }
 *
 * // Expression
 * { '@$': 'minVal' }
 * // ->
 * 100
 */
export interface GetVariable<out T = any> {
  /** {@link GetVariable} */
  readonly [Key.GetVariable]: query.Variable.Name
}
export namespace GetVariable {
  export const isGetVariable: TypeChecker<GetVariable> =
    TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.object<GetVariable>({
        [Key.GetVariable]: query.Variable.Name.isName,
      }),
    ).usingAlias('Fn.GetVariable')
  export const assertIsGetVariable: TypeAsserter<GetVariable> =
    TypeAsserter.Factory.from(isGetVariable)

  /**
   * @see {@link Node}
   */
  export class Node<T = any> extends fnNode.Node<
    T,
    Key.GetVariable,
    { readonly name: query.Variable.Name }
  > {
    // TODO: can we figure out what the type should be?
    public readonly type = undefined

    constructor(arg: Node<T>['arg']) {
      super(Key.GetVariable, arg)
    }
  }
  export namespace Node {
    export const isNode: TypeChecker<Node> = TypeChecker.Factory.instance(Node)
    export const assertIsNode: TypeAsserter<Node> =
      TypeAsserter.Factory.from(isNode)
  }

  export const HELPER: FnHelper<GetVariable> = {
    createNode: (fn, context) => {
      const alias = fn[Key.GetVariable]
      // TODO: handle saved queries that don't yet have variable values
      if (query.Variable.Name.Reserved.isReserved(alias)) {
        // Reserved variables will be replaced with values later
        return new Node({ name: alias })
      }
      return new expressionNode.ForValue(context.variables[alias])
    },
    expressionsInArgs: fn => [],
    typeChecker: isGetVariable,
  }
}
